import React from 'react';
import Box from '@amzn/meridian/box';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../../hooks/useMessages';
import InputBar from '../InputBar/InputBar';
import JobStatusCard from '../JobStatusCard/JobStatusCard';
import ConfirmationCard from '../ConfirmationCard/ConfirmationCard';
import { ConfirmationDecision } from '../../service/ordisApiService';
import './MessageContainer.css';

interface MessageContainerProps {
  messages: ChatMessage[];
  isLoading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onSendMessage: (message: string) => void;
  onRespondToConfirmation: (token: string, decision: ConfirmationDecision) => void;
}

const MessageContainer: React.FC<MessageContainerProps> = ({
  messages,
  isLoading,
  messagesEndRef,
  onSendMessage,
  onRespondToConfirmation,
}) => {
  return (
    <Box className="message-container">
      <Box className="messages-list">
        {messages.map((message) => (
          <Box
            key={message.id}
            className={`message-bubble ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
          >
            <Box className="message-header">
              <span className="message-sender">
                {message.sender === 'user' ? 'You' : 'Ordis'}
              </span>
              <span className="message-timestamp">
                {message.timestamp.toLocaleTimeString()}
              </span>
            </Box>
            <Box className="message-text">
              {message.sender === 'bot' ? (
                <ReactMarkdown
                  components={{
                    // Render links to open in a new tab and inherit safe styling
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer" />
                    ),
                  }}
                >
                  {message.text}
                </ReactMarkdown>
              ) : (
                message.text
              )}
              {message.downloadUrl && (
                <div style={{ marginTop: '8px' }}>
                  <a
                    href={message.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0EA5E9', textDecoration: 'underline', fontWeight: 500 }}
                  >
                    📥 Download Report
                  </a>
                </div>
              )}
              {message.jobId && <JobStatusCard jobId={message.jobId} />}
              {message.confirmation && (
                <ConfirmationCard
                  token={message.confirmation.token}
                  details={message.confirmation.details}
                  onRespond={onRespondToConfirmation}
                />
              )}
            </Box>
          </Box>
        ))}
        
        {isLoading && (
          <Box className="message-bubble bot-message loading-message">
            <Box className="message-header">
              <span className="message-sender">Ordis</span>
            </Box>
            <Box className="message-text">
              <span className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </Box>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>
      
      <InputBar onSendMessage={onSendMessage} isLoading={isLoading} />
    </Box>
  );
};

export default MessageContainer;
